import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    slug: '', 
    display_name: '',
    description: '',
    system_prompt: 'Du bist ein hilfsreicher KI-Assistent.',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`);
      setCompanies(res.data);
      setFilteredCompanies(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Firmen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Firmen', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadCompanies(); 
  }, []);

  useEffect(() => {
    const filtered = companies.filter(company =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCompanies(filtered);
  }, [searchTerm, companies]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`, form);
      setOpen(false);
      resetForm();
      await loadCompanies();
      setSnackbar({ open: true, message: 'Firma erfolgreich erstellt', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Erstellen der Firma:', error);
      setSnackbar({ open: true, message: 'Fehler beim Erstellen der Firma', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/${editingCompany.id}`, form);
      setOpen(false);
      setEditingCompany(null);
      resetForm();
      await loadCompanies();
      setSnackbar({ open: true, message: 'Firma erfolgreich aktualisiert', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Firma:', error);
      setSnackbar({ open: true, message: 'Fehler beim Aktualisieren der Firma', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sind Sie sicher, dass Sie diese Firma löschen möchten?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/${id}`);
        await loadCompanies();
        setSnackbar({ open: true, message: 'Firma erfolgreich gelöscht', severity: 'success' });
      } catch (error) {
        console.error('Fehler beim Löschen der Firma:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Löschen der Firma';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      }
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setForm({
      name: company.name,
      slug: company.slug,
      display_name: company.display_name,
      description: company.description || '',
      system_prompt: company.system_prompt,
      is_active: company.is_active
    });
    setOpen(true);
  };

  const resetForm = () => {
    setForm({ 
      name: '', 
      slug: '', 
      display_name: '',
      description: '',
      system_prompt: 'Du bist ein hilfsreicher KI-Assistent.',
      is_active: true
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCompany(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Firmen</Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Neue Firma
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Firmen durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            {filteredCompanies.length} von {companies.length} Firmen angezeigt
          </Typography>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell><strong>Firmenname</strong></TableCell>
              <TableCell><strong>Anzeigename</strong></TableCell>
              <TableCell><strong>Slug</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Erstellt</strong></TableCell>
              <TableCell align="center"><strong>Aktionen</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCompanies.map((company) => (
              <TableRow key={company.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1" fontWeight="medium">
                      {company.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {company.display_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={company.slug} 
                    size="small" 
                    variant="outlined"
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={company.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                    label={company.is_active ? 'Aktiv' : 'Inaktiv'}
                    color={company.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(company.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="Bearbeiten">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(company)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(company.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredCompanies.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'Keine Firmen gefunden' : 'Noch keine Firmen vorhanden'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff' : 'Erstellen Sie Ihre erste Firma'}
          </Typography>
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {editingCompany ? 'Firma bearbeiten' : 'Neue Firma erstellen'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Firmenname" 
                  value={form.name} 
                  onChange={e => setForm({ ...form, name: e.target.value })} 
                  fullWidth 
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Slug (URL-Identifier)" 
                  value={form.slug} 
                  onChange={e => setForm({ ...form, slug: e.target.value })} 
                  fullWidth 
                  required
                  helperText="Wird in der URL verwendet (z.B. firma-name)"
                />
              </Grid>
            </Grid>
            
            <TextField 
              label="Anzeigename" 
              value={form.display_name} 
              onChange={e => setForm({ ...form, display_name: e.target.value })} 
              fullWidth 
              required
              helperText="Name, der in der Benutzeroberfläche angezeigt wird"
            />
            
            <TextField 
              label="Beschreibung" 
              value={form.description} 
              onChange={e => setForm({ ...form, description: e.target.value })} 
              fullWidth 
              multiline 
              rows={2}
            />
            
            <TextField 
              label="System-Prompt" 
              value={form.system_prompt} 
              onChange={e => setForm({ ...form, system_prompt: e.target.value })} 
              fullWidth 
              multiline 
              rows={3}
              helperText="Anweisungen für das KI-Modell"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Firma ist aktiv"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button 
            onClick={editingCompany ? handleUpdate : handleCreate} 
            disabled={saving} 
            variant="contained"
          >
            {saving ? 'Speichern...' : (editingCompany ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;
